/**
 * Chart Registry — single source of truth for all performance charts.
 *
 * To add a new chart:
 *   1. Create the React component in charts/NewChart.js
 *   2. Add one entry to CHART_REGISTRY below
 *   3. Export the component from charts/index.js
 *   4. Add the backend route (separate concern)
 *
 * That's it. No changes to PortfolioPerformance.js needed.
 *
 * Each entry describes:
 *   - id / label / section / conditional  (metadata for drawer & preferences)
 *   - component          (React component to render)
 *   - sectionTitle/Subtitle  (ChartSection wrapper; null = no wrapper)
 *   - propsMapper(state)     (extracts props from parent state snapshot)
 *   - isDataReady(state)     (returns true if chart can render)
 *   - apiEndpoint / apiParams / dataKey  (for advanced chart data fetch; null for core)
 */

import SummaryCards from './SummaryCards';
import GenerationBarChart from './GenerationBarChart';
import RevenueBarChart from './RevenueBarChart';
import PriceLineChart from './PriceLineChart';
// Curtailment has moved to its own dedicated page — PortfolioCurtailment.js.
import CapturePriceChart from './CapturePriceChart';
import PriceDurationChart from './PriceDurationChart';
import NegativePricingHeatmap from './NegativePricingHeatmap';
import CapacityFactorHeatmap from './CapacityFactorHeatmap';
import RevenueAtRiskChart from './RevenueAtRiskChart';
import UtilizationChart from './UtilizationChart';
import ForecastAccuracyChart from './ForecastAccuracyChart';
import BiddingStrategyChart from './BiddingStrategyChart';
import YoYChart from './YoYChart';
const CHART_REGISTRY = [
  // ── Core charts (data comes from the single /performance API) ──────────

  {
    id: 'summary-cards',
    label: 'Summary Cards',
    section: 'core',
    conditional: null,
    component: SummaryCards,
    sectionTitle: null,
    sectionSubtitle: null,
    propsMapper: (state) => ({
      generation: state.data?.generation,
      revenue: state.data?.revenue,
      price: state.data?.price,
    }),
    isDataReady: (state) => !!state.data,
    apiEndpoint: null,
    apiParams: null,
    dataKey: null,
  },

  {
    id: 'generation',
    label: 'Generation',
    section: 'core',
    conditional: null,
    component: GenerationBarChart,
    sectionTitle: null,
    sectionSubtitle: null,
    propsMapper: (state) => ({
      generation: state.data?.generation,
      assets: state.data?.assets,
      frequency: state.data?.frequency,
    }),
    isDataReady: (state) => !!state.data,
    apiEndpoint: null,
    apiParams: null,
    dataKey: null,
  },

  {
    id: 'revenue',
    label: 'Revenue',
    section: 'core',
    conditional: null,
    component: RevenueBarChart,
    sectionTitle: 'Revenue',
    sectionSubtitle: 'Estimated revenue = generation (MWh) x dispatch price (A$/MWh)',
    propsMapper: (state) => ({
      revenue: state.data?.revenue,
      assets: state.data?.assets,
      frequency: state.data?.frequency,
    }),
    isDataReady: (state) => !!state.data,
    apiEndpoint: null,
    apiParams: null,
    dataKey: null,
  },

  {
    id: 'regional-price',
    label: 'Regional Price',
    section: 'core',
    conditional: null,
    component: PriceLineChart,
    sectionTitle: 'Regional Price',
    sectionSubtitle: 'Average wholesale electricity price (A$/MWh) per region',
    propsMapper: (state) => ({
      price: state.data?.price,
      frequency: state.data?.frequency,
    }),
    isDataReady: (state) => !!state.data,
    apiEndpoint: null,
    apiParams: null,
    dataKey: null,
  },

  // (Curtailment chart moved to its own dedicated page — PortfolioCurtailment.)

  // ── Advanced charts (each has its own API endpoint) ────────────────────

  {
    id: 'capture-price',
    label: 'Capture Price',
    section: 'advanced',
    conditional: null,
    component: CapturePriceChart,
    sectionTitle: 'Capture Price',
    sectionSubtitle: 'Generation-weighted average price per asset vs flat regional average. Shows what your assets actually earn per MWh.',
    propsMapper: (state) => ({
      data: state.advancedData['capture-price']?.capture_price,
      assets: state.advancedData['capture-price']?.assets,
      frequency: state.advancedData['capture-price']?.frequency || state.frequency,
    }),
    isDataReady: (state) => !!state.advancedData['capture-price'],
    apiEndpoint: '/capture-price',
    apiParams: (start, end, frequency) => ({ start, end, frequency }),
    dataKey: 'capture-price',
  },

  {
    id: 'price-duration',
    label: 'Price Duration Curve',
    section: 'advanced',
    conditional: null,
    component: PriceDurationChart,
    sectionTitle: 'Price Duration Curve',
    sectionSubtitle: 'Sorted price curve showing % of time price exceeds each level. The industry standard tool for price risk analysis.',
    propsMapper: (state) => ({
      data: state.advancedData['price-duration']?.price_duration,
      regions: state.advancedData['price-duration']?.regions,
    }),
    isDataReady: (state) => !!state.advancedData['price-duration'],
    apiEndpoint: '/price-duration',
    apiParams: (start, end) => ({ start, end }),
    dataKey: 'price-duration',
  },

  {
    id: 'negative-pricing',
    label: 'Negative Pricing Heatmap',
    section: 'advanced',
    conditional: null,
    component: NegativePricingHeatmap,
    sectionTitle: 'Negative Pricing Heatmap',
    sectionSubtitle: 'Frequency of negative prices by hour-of-day. Identifies optimal battery charge/discharge windows.',
    propsMapper: (state) => ({
      data: state.advancedData['negative-pricing']?.negative_pricing,
      regions: state.advancedData['negative-pricing']?.regions,
      granularity: state.advancedData['negative-pricing']?.granularity || 'monthly',
    }),
    isDataReady: (state) => !!state.advancedData['negative-pricing'],
    apiEndpoint: '/negative-pricing',
    apiParams: (start, end) => ({ start, end }),
    dataKey: 'negative-pricing',
  },

  {
    id: 'capacity-factor',
    label: 'Capacity Factor',
    section: 'advanced',
    conditional: null,
    component: CapacityFactorHeatmap,
    sectionTitle: 'Capacity Factor',
    sectionSubtitle: 'Monthly capacity factor (%) per asset. Shows seasonal performance patterns and underperforming assets.',
    propsMapper: (state) => ({
      data: state.advancedData['capacity-factor']?.capacity_factors,
      assets: state.advancedData['capacity-factor']?.assets,
    }),
    isDataReady: (state) => !!state.advancedData['capacity-factor'],
    apiEndpoint: '/capacity-factors',
    apiParams: (start, end) => ({ start, end }),
    dataKey: 'capacity-factor',
  },

  {
    id: 'revenue-at-risk',
    label: 'Revenue at Risk',
    section: 'advanced',
    conditional: null,
    component: RevenueAtRiskChart,
    sectionTitle: 'Revenue at Risk',
    sectionSubtitle: 'Revenue distribution by price band. Shows how much revenue depends on extreme price events vs baseload.',
    propsMapper: (state) => ({
      data: state.advancedData['revenue-at-risk']?.revenue_at_risk,
    }),
    isDataReady: (state) => !!state.advancedData['revenue-at-risk'],
    apiEndpoint: '/revenue-at-risk',
    apiParams: (start, end) => ({ start, end }),
    dataKey: 'revenue-at-risk',
  },

  {
    id: 'utilization',
    label: 'Asset Utilization',
    section: 'advanced',
    conditional: null,
    component: UtilizationChart,
    sectionTitle: 'Asset Utilization',
    sectionSubtitle: 'Available capacity vs actual dispatch (MW). Shows how much capacity sits idle.',
    propsMapper: (state) => ({
      data: state.advancedData['utilization']?.utilization,
      assets: state.advancedData['utilization']?.assets,
      frequency: state.advancedData['utilization']?.frequency || state.frequency,
    }),
    isDataReady: (state) => !!state.advancedData['utilization'],
    apiEndpoint: '/utilization',
    apiParams: (start, end, frequency) => ({ start, end, frequency }),
    dataKey: 'utilization',
  },

  {
    id: 'forecast-accuracy',
    label: 'Forecast Accuracy',
    section: 'advanced',
    conditional: null,
    component: ForecastAccuracyChart,
    sectionTitle: 'Forecast Accuracy',
    sectionSubtitle: 'UIGF forecast (with POE10/90 bands) vs actual dispatch for solar/wind assets. Data from Aug 2024+.',
    propsMapper: (state) => ({
      data: state.advancedData['forecast-accuracy']?.forecast_accuracy,
      assets: state.advancedData['forecast-accuracy']?.assets,
      frequency: state.advancedData['forecast-accuracy']?.frequency || state.frequency,
    }),
    isDataReady: (state) => {
      const d = state.advancedData['forecast-accuracy'];
      return d && d.forecast_accuracy?.length > 0;
    },
    apiEndpoint: '/forecast-accuracy',
    apiParams: (start, end, frequency) => ({ start, end, frequency }),
    dataKey: 'forecast-accuracy',
  },

  {
    id: 'bidding-strategy',
    label: 'Bidding Strategy',
    section: 'advanced',
    conditional: null,
    component: BiddingStrategyChart,
    sectionTitle: 'Bidding Strategy',
    sectionSubtitle: 'Bid stack over time — average MW offered across 10 NEM price bands. Shows market positioning.',
    propsMapper: (state) => ({
      data: state.advancedData['bidding-strategy']?.bidding_strategy,
      assets: state.advancedData['bidding-strategy']?.assets,
      frequency: state.advancedData['bidding-strategy']?.frequency || state.frequency,
    }),
    isDataReady: (state) => {
      const d = state.advancedData['bidding-strategy'];
      return d && d.bidding_strategy?.length > 0;
    },
    apiEndpoint: '/bidding-strategy',
    apiParams: (start, end, frequency) => ({ start, end, frequency }),
    dataKey: 'bidding-strategy',
  },

  {
    id: 'yoy',
    label: 'Year-over-Year',
    section: 'advanced',
    conditional: null,
    component: YoYChart,
    sectionTitle: 'Year-over-Year Comparison',
    sectionSubtitle: 'Monthly generation and revenue overlaid by year. Reveals multi-year trends and seasonal patterns.',
    propsMapper: (state) => ({
      generationData: state.advancedData['yoy']?.yoy_generation,
      revenueData: state.advancedData['yoy']?.yoy_revenue,
    }),
    isDataReady: (state) => !!state.advancedData['yoy'],
    apiEndpoint: '/yoy',
    apiParams: (start, end, frequency, startDate, endDate) => {
      const startYear = parseInt(startDate.slice(0, 4), 10);
      const endYear = parseInt(endDate.slice(0, 4), 10);
      const yoyStartYear = Math.min(startYear, 2020);
      return { start_year: yoyStartYear, end_year: endYear };
    },
    dataKey: 'yoy',
  },

];

export default CHART_REGISTRY;

/** O(1) lookup by chart id. */
export const CHART_REGISTRY_LOOKUP = {};
CHART_REGISTRY.forEach(entry => { CHART_REGISTRY_LOOKUP[entry.id] = entry; });

/** Flat config array consumed by useChartPreferences and the drawer (id, label, section, conditional only). */
export const DEFAULT_CHART_CONFIG = CHART_REGISTRY.map(({ id, label, section, conditional }) => ({
  id, label, section, conditional,
}));
